import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DataTable } from '~/components/common/data-table/data-table';
import type {
  DataTableColumn,
  DataTableState,
} from '~/components/common/data-table/data-table.types';
import { useRunTranslations } from '~/features/catalog-operations/hooks/use-translations';
import { useDebouncedValue } from '~/hooks/use-debounced-value';
import { useCanTranslateContent } from '~/hooks/useCanTranslateContent';
import { extractApiError } from '~/utils/extract-api-error';

import { useBrowseUrlState } from '../hooks/use-browse-url-state';
import { BROWSE_PAGE_SIZE, useExportBrowse } from '../hooks/use-export-browse';
import { useExportConnectorTemplates } from '../hooks/use-export-templates';
import { useExportTenantContext } from '../hooks/use-export-tenant-context';
import type {
  ExportConnector,
  ExportFormat,
  ExportTemplatePreviewRow,
} from '../types/export.types';
import { supportsTemplateTrigger } from '../types/export.types';
import type { BrowseExportFilters } from '../utils/export-browse-filters';
import { toExportFilters } from '../utils/export-browse-filters';
import {
  canGoToNextPage,
  cursorForPage,
  initialCursors,
  withNextCursor,
} from '../utils/export-browse-paging';
import {
  availableColumns,
  isIdentifierOnlySelection,
  resolveDefaultSelection,
  selectionToFields,
  shownColumnKeys,
  standardDefaultFields,
} from '../utils/export-column-selection';

import { BrowseExportBar } from './browse/browse-export-bar';
import { BrowseToolbar } from './browse/browse-toolbar';
import { ExportRowCompareSheet } from './export-row-compare-sheet';

/** A browse row plus its position in the loaded page, which is its stable key —
 *  the identifier cell could repeat, and the API's order is the file's order. */
interface BrowseRow {
  key: string;
  position: number;
  row: ExportTemplatePreviewRow;
}

/** Stands in for an unresolved tenant so the request body keeps its shape. The
 *  query is disabled in that state, so this is never actually sent. */
const EMPTY_TENANT = { subsidiaryId: '', organizationId: '', subsidiaryCode: '' } as const;

/** This page turns on no run options. */
const NO_RUN_OPTIONS: ReadonlySet<string> = new Set();

/** Page sizes offered. The browse endpoint bounds `pageSize` to 1–200. */
const PAGE_SIZE_OPTIONS = [50, BROWSE_PAGE_SIZE, 200];

interface ExportBrowseViewProps {
  sourceWorkflowId: string;
  /** Known from the drawer's link; otherwise resolved from the response, which
   *  echoes the run's connector — so a bare URL works. */
  connector?: ExportConnector;
  /** Human context for the progress toast; the run id when the URL has none. */
  label: string;
}

/**
 * The full-page export workbench: every product of a run, as the export would
 * write it, with the controls to narrow it and the button to export it.
 *
 * Exists because the drawer cannot answer "find me this product" — its preview
 * is capped at 50 rows by the API on purpose — and cannot show 51 columns
 * readably at any row count. This page pages the whole run, and the export it
 * starts carries the same `fields`, `filters` and `template` the table shows.
 *
 * **Three things narrow what you see, and they do not reach equally far:**
 *
 *   - *Export filters* (successful only, score range, categories, product ids)
 *     run in SQL on both the browse and the trigger — a product shown under
 *     them is a product the file will contain.
 *   - *Search* and the *header-row filters* run in SQL too, but only the browse
 *     endpoint accepts them. They narrow the table, never the file, and the
 *     export bar says so whenever one is active.
 *   - The *column picker* changes which columns are read, shown and exported.
 *     It defaults to the attributes the run actually enriched (`runFields`).
 *
 * Working state lives in the URL (`useBrowseUrlState`) so a reload or a shared
 * link lands on the same view. Search text, the header-row filters and a
 * pasted id list stay local: transient, or too large for a URL.
 */
export function ExportBrowseView({ sourceWorkflowId, connector, label }: ExportBrowseViewProps) {
  const { t } = useTranslation('exports');
  const tenantContext = useExportTenantContext();
  const { search: urlState, patch } = useBrowseUrlState();

  const [pageIndex, setPageIndex] = useState(0);
  const [cursors, setCursors] = useState(initialCursors);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput, 400);
  const [productIds, setProductIds] = useState<string[]>([]);
  const [compareRow, setCompareRow] = useState<BrowseRow | null>(null);
  // Raw header-row inputs, debounced before they reach the request for the same
  // reason search is: each change costs a page read plus a count.
  const [columnFilterInput, setColumnFilterInput] = useState<Record<string, string>>({});
  const columnFilters = useDebouncedValue(columnFilterInput, 400);

  /** Any change to what the result set IS invalidates every recorded cursor:
   *  page 3 of one filter is not page 3 of another. */
  const resetPaging = () => {
    setPageIndex(0);
    setCursors(initialCursors);
  };

  // ── URL-held state, read straight from the route. `fields` and `categories`
  // are comma-joined in the URL; memoised on the string so a rerender does not
  // hand the query a fresh array of the same keys.
  const template = urlState.template ?? null;
  const pageSize = urlState.pageSize ?? BROWSE_PAGE_SIZE;
  const columnKeys = useMemo(
    () => (urlState.fields ? urlState.fields.split(',').filter(Boolean) : null),
    [urlState.fields],
  );
  // Languages this run can be exported in: only those it was actually translated into
  // (batch `targetLocales` or a later translate), with at least one exportable product —
  // QA-held rows are left out of a locale export, so a locale of only those offers nothing.
  // GMC only; internal users only. Empty ⇒ the Language filter is not shown at all.
  const canTranslate = useCanTranslateContent();
  // Keyed on the run alone: a META run has no translations, so its summary is empty.
  const runTranslations = useRunTranslations(sourceWorkflowId, {
    enabled: canTranslate && connector !== 'META',
  });
  const exportableLocales = useMemo(
    () => [
      ...new Set(
        (runTranslations.data ?? [])
          .filter((r) => r.passCount + r.flagCount > 0)
          .map((r) => r.locale),
      ),
    ],
    [runTranslations.data],
  );
  // A `locale` in the URL the run does not have (a stale or hand-edited link) is ignored
  // once the summary has loaded — otherwise the file would be filtered by a locale the
  // hidden Language control cannot show or clear. Kept while loading, so a shared link
  // opens straight on its translated view.
  // (A disabled query also reports `pending`, hence the `canTranslate` guard.)
  const summaryLoading = canTranslate && runTranslations.isPending;
  const runHasUrlLocale = exportableLocales.some(
    (l) => l.toLowerCase() === urlState.locale?.toLowerCase(),
  );
  const urlLocale =
    urlState.locale && (summaryLoading || runHasUrlLocale) ? urlState.locale : undefined;
  const exportFilters: BrowseExportFilters = useMemo(
    () => ({
      successOnly: urlState.successOnly ?? true,
      ...(urlState.minScore !== undefined ? { minScore: urlState.minScore } : {}),
      ...(urlState.maxScore !== undefined ? { maxScore: urlState.maxScore } : {}),
      categories: urlState.categories ? urlState.categories.split(',').filter(Boolean) : [],
      productIds,
      ...(urlLocale ? { locale: urlLocale } : {}),
    }),
    [
      urlState.successOnly,
      urlState.minScore,
      urlState.maxScore,
      urlState.categories,
      urlLocale,
      productIds,
    ],
  );
  const filters = useMemo(() => toExportFilters(exportFilters), [exportFilters]);

  const cursor = cursorForPage(cursors, pageIndex) ?? null;

  // A blank input is dropped rather than sent: the server ignores it anyway, and
  // keeping it out of the request keeps it out of the query key too, so clearing
  // a filter returns to a cached page instead of refetching.
  const activeColumnFilters = useMemo(
    () => Object.fromEntries(Object.entries(columnFilters).filter(([, v]) => v.trim() !== '')),
    [columnFilters],
  );
  const viewFilterCount = Object.keys(activeColumnFilters).length;
  const viewFiltersActive = search !== '' || viewFilterCount > 0;

  const { data, isPending, isFetching, isError, error } = useExportBrowse({
    request: {
      sourceWorkflowId,
      tenantContext: tenantContext ?? EMPTY_TENANT,
      fields: columnKeys,
      ...(template ? { template } : {}),
      filters,
      pageSize,
      cursor,
      ...(search ? { search } : {}),
      ...(viewFilterCount > 0 ? { columnFilters: activeColumnFilters } : {}),
    },
    enabled: Boolean(sourceWorkflowId) && tenantContext !== null,
  });
  const nextCursor = data?.nextCursor ?? null;

  const activeConnector = connector ?? data?.connectorCode;
  const {
    transformationsByCode,
    templates,
    formats,
    connector: connectorEntry,
    defaultTemplateCode,
  } = useExportConnectorTemplates(activeConnector ?? 'GMC', Boolean(activeConnector));

  // Option-gated columns are left out: this page sends no `options`, so the server
  // would refuse them ("Unknown META fields").
  const allColumns = useMemo(
    () => availableColumns(connectorEntry?.columns ?? [], NO_RUN_OPTIONS),
    [connectorEntry],
  );
  const columnsByKey = useMemo(
    () => Object.fromEntries(allColumns.map((c) => [c.key, c])),
    [allColumns],
  );

  // The run's own attribute selection, echoed by the response so this page works
  // from a bare URL. Empty means "not recorded", and `resolveDefaultSelection`
  // then falls back to every column rather than to an identifiers-only table.
  const runFields = data?.runFields ?? [];
  const runFieldsKey = runFields.join(',');
  const fallbackFields = standardDefaultFields(connector ?? data?.connectorCode);
  const defaultSelection = useMemo(
    () =>
      resolveDefaultSelection({
        columns: allColumns,
        batchAttributeKeys: runFields,
        fallbackKeys: fallbackFields,
      }),
    // `runFieldsKey` stands in for the array so a fresh array of the same keys
    // does not rebuild the selection on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allColumns, runFieldsKey, fallbackFields],
  );
  const effectiveColumnKeys = columnKeys ?? defaultSelection.selected;

  const selectedTemplate = templates.find((tpl) => tpl.code === template);
  const encodedKeys = useMemo(
    () => new Set(Object.keys(selectedTemplate?.transformations ?? {})),
    [selectedTemplate],
  );

  // ── The export request, built from the same inputs as the table. A full
  // column selection sends `null` (see `selectionToFields`); the default
  // template stays off the wire, as the drawer does.
  const fieldsToSend = useMemo(
    () => selectionToFields(effectiveColumnKeys, allColumns),
    [effectiveColumnKeys, allColumns],
  );
  const templateToSend = template && template !== defaultTemplateCode ? template : null;
  const exportRequest = useMemo(
    () => ({
      sourceWorkflowId,
      tenantContext: tenantContext ?? EMPTY_TENANT,
      filters,
      fields: fieldsToSend,
      ...(templateToSend ? { template: templateToSend } : {}),
    }),
    [sourceWorkflowId, tenantContext, filters, fieldsToSend, templateToSend],
  );
  const configDefaultFormat = formats.find((f) => f.default)?.code ?? 'CSV';
  const format: ExportFormat = urlState.format ?? configDefaultFormat;
  const identifierOnly = isIdentifierOnlySelection(effectiveColumnKeys, allColumns);
  const templateUnsupported = !supportsTemplateTrigger() && templateToSend != null;

  // Only the selected columns are drawn. The server always emits the internal
  // `id` whatever `fields` asks for; drawing it when it was not chosen would
  // show a column the operator deselected. The template's other identifiers are
  // drawn regardless — the picker shows them as always included (see
  // `shownColumnKeys`). `columnIndex` stays the position in the response, which
  // is what `encoded` is indexed by.
  const header = useMemo(() => {
    const chosen = shownColumnKeys(effectiveColumnKeys, selectedTemplate);
    return (data?.header ?? [])
      .map((key, columnIndex) => ({ key, columnIndex }))
      .filter(({ key }) => chosen.has(key));
  }, [data, effectiveColumnKeys, selectedTemplate]);
  const headerKeys = useMemo(() => header.map(({ key }) => key), [header]);

  const rows: BrowseRow[] = useMemo(
    () =>
      (data?.rows ?? []).map((row, index) => ({
        key: `${row.encoded[0] ?? ''}-${String(pageIndex)}-${String(index)}`,
        position: index,
        row,
      })),
    [data, pageIndex],
  );

  const dataColumns: DataTableColumn<BrowseRow>[] = useMemo(
    () =>
      header.map(({ key, columnIndex }, position) => {
        const column = columnsByKey[key];
        const encoded = encodedKeys.has(key);
        return {
          id: key,
          // Always two lines — label, then key with the FORMATTED marker beside
          // it — so every header is the same height and the labels line up
          // whether or not the template rewrites a column.
          header: (
            <span className="block">
              <span className="text-foreground block whitespace-nowrap">
                {column?.label ?? key}
              </span>
              <span className="flex items-center gap-1.5 whitespace-nowrap">
                <span className="text-muted-foreground font-mono text-[11px] font-normal">
                  {key}
                </span>
                {encoded && (
                  <span className="text-accent-main text-[10px] font-semibold">
                    {t('preview.file.encodedMarker')}
                  </span>
                )}
              </span>
            </span>
          ),
          // Empty renders empty, as in the drawer: the file has nothing there,
          // and a glyph would read as a value the export does not contain.
          cell: (item) => {
            const value = item.row.encoded[columnIndex] ?? '';
            return (
              <span className="block truncate font-mono text-xs" title={value || undefined}>
                {value}
              </span>
            );
          },
          accessorFn: (item) => item.row.encoded[columnIndex] ?? '',
          // Only where the server would accept a filter. Drawing an input for a
          // JSON or derived column would 400, and an input that cannot work is
          // worse than one that was never offered.
          enableFiltering: column?.filterable === true,
          // The API's order IS the file's order, and paging depends on it, so a
          // client-side re-sort of one page would misrepresent both.
          enableSorting: false,
          width: position === 0 ? 300 : 220,
        };
      }),
    [header, columnsByKey, encodedKeys, t],
  );

  /** The table reports its whole state; only the header-row filters are ours to
   *  forward. Compared before storing so a state change that did not touch the
   *  filters cannot reset paging or refetch. */
  const handleStateChange = (state: DataTableState) => {
    const next = Object.fromEntries(state.columnFilters.map((f) => [f.id, f.value]));
    setColumnFilterInput((current) => {
      if (JSON.stringify(current) === JSON.stringify(next)) return current;
      resetPaging();
      return next;
    });
  };

  if (isError) {
    return <p className="text-error text-sm">{extractApiError(error, t('browse.error'))}</p>;
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <BrowseToolbar
        searchInput={searchInput}
        onSearchChange={(value) => {
          setSearchInput(value);
          resetPaging();
        }}
        exportFilters={exportFilters}
        languageOptions={exportableLocales}
        onExportFiltersChange={(next) => {
          setProductIds(next.productIds);
          patch({
            successOnly: next.successOnly ? undefined : false,
            minScore: next.minScore,
            maxScore: next.maxScore,
            categories: next.categories.length > 0 ? next.categories.join(',') : undefined,
            locale: next.locale,
          });
          resetPaging();
        }}
        viewFilterCount={viewFilterCount}
        onClearViewFilters={() => {
          setColumnFilterInput({});
          resetPaging();
        }}
        templates={templates}
        defaultTemplateCode={defaultTemplateCode}
        template={template}
        onTemplateChange={(code) => {
          patch({ template: code ?? undefined });
          resetPaging();
        }}
        columns={allColumns}
        selectedColumns={effectiveColumnKeys}
        onColumnsChange={(next) => {
          patch({ fields: next.join(',') });
          // The rows are unchanged, but a cursor is only meaningful against the
          // request that produced it.
          resetPaging();
        }}
        runFields={runFields.length > 0 ? runFields : undefined}
        fallbackFields={fallbackFields}
        unmatchedColumns={defaultSelection.unmatched}
        presets={connectorEntry?.fieldPresets ?? []}
        transformations={selectedTemplate?.transformations ?? {}}
        total={data?.total}
        isPending={isPending}
        isFetching={isFetching}
      />

      <DataTable<BrowseRow>
        mode="server"
        columns={dataColumns}
        data={rows}
        rowKey={(item) => item.key}
        totalRows={data?.total ?? 0}
        isLoading={isPending}
        skeletonRows={10}
        pagination={{ pageIndex, pageSize }}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        onPaginationChange={(next) => {
          // A new page size is a new result set for cursor purposes: back to
          // page one, and the size travels in the URL like the other choices.
          if (next.pageSize !== pageSize) {
            patch({ pageSize: next.pageSize === BROWSE_PAGE_SIZE ? undefined : next.pageSize });
            resetPaging();
            return;
          }
          // Going forward records the cursor for the page being opened, from the
          // current page's `nextCursor`, which is already in hand here. A page
          // with no recorded cursor must never be requested — it would come back
          // as page one under a later page's number. One step either way,
          // because `hidePageNumbers` removes the arbitrary jump.
          if (next.pageIndex > pageIndex) {
            if (nextCursor === null) return;
            setCursors((current) => withNextCursor(current, pageIndex, nextCursor));
            setPageIndex(next.pageIndex);
            return;
          }
          if (!canGoToNextPage(cursors, next.pageIndex - 1) && next.pageIndex > 0) return;
          setPageIndex(next.pageIndex);
        }}
        onStateChange={handleStateChange}
        hidePageNumbers
        enableFiltering
        enableColumnResizing
        fillHeight
        tableItemType={t('browse.itemType')}
        emptyMessage={viewFiltersActive ? t('browse.noMatches') : t('browse.empty')}
        onRowClick={setCompareRow}
        paginationClassName="pt-2"
      />

      {tenantContext && activeConnector && (
        <BrowseExportBar
          sourceWorkflowId={sourceWorkflowId}
          label={label}
          connector={activeConnector}
          request={exportRequest}
          formats={formats}
          format={format}
          onFormatChange={(next) => {
            patch({ format: next === configDefaultFormat ? undefined : next });
          }}
          feedTemplateApplied={templateToSend != null}
          viewFiltersActive={viewFiltersActive}
          identifierOnly={identifierOnly}
          templateUnsupported={templateUnsupported}
        />
      )}

      <ExportRowCompareSheet
        row={compareRow?.row ?? null}
        onClose={() => {
          setCompareRow(null);
        }}
        header={headerKeys}
        columnsByKey={columnsByKey}
        transformationsByCode={transformationsByCode}
        template={selectedTemplate}
        specUrl={connectorEntry?.specUrl ?? null}
        position={(compareRow?.position ?? 0) + 1}
      />
    </div>
  );
}
